import React, { useMemo } from 'react';
import { formatMoney } from '@/utils/formatPayment';
import { LineItemsWrapper } from '@/common/LineItems/style';
import { useTranslation } from 'react-i18next';
import { useMobile, useTabletOrMobile } from '@/hooks/useMediaQuery';
import { LineItems as LineItemsComponent, LineItemsColumn, LineItemsDataSource } from '@airwallex/line-items';
import DiscountCell from '@/common/DiscountCell';
import { CreditNoteDetail, CreditNoteLineItem } from '../types';
import { formatPriceCurrency } from '@billing/billing-domain-shared';
import {
  HeaderText,
  DiscountColumnTooltip,
  LineItemColKey as ColKey,
  ShowMoreRow,
  getCommonWidth,
} from '@/common/LineItems';
import { useShowMoreItems } from '@/hooks/useShowMoreItems';
import TaxTooltip from '@/components/TaxTooltip';
import { VStack } from '@airwallex/stacks';
import { Text } from '@airwallex/typography';
import {
  creditNoteHasInvoiceDiscount,
  creditNoteHasLineItemDiscount,
  creditWithLineItem,
} from '@/utils/creditNoteLayout';

const createCreditNoteItemRow = (item: CreditNoteLineItem, hasDiscount: boolean): LineItemsDataSource<ColKey> => {
  const creditWithItem = creditWithLineItem(item);

  const row = {
    product: item.label ? (
      <VStack>
        <Text variant="body" title={item.label}>
          {item.label}
        </Text>
        {!creditWithItem && item.descriptionV2 ? (
          <Text variant="hint" color="secondary" title={item.descriptionV2}>
            {item.descriptionV2}
          </Text>
        ) : null}
      </VStack>
    ) : (
      item.description ?? '-'
    ),
    quantity: creditWithItem ? item.quantity : '-',
    unitPrice: creditWithItem
      ? formatPriceCurrency({
          amount: item.unitAmountExcludeTax ?? 0,
          currency: item.currency,
          showSymbol: true,
          showCode: false,
        })
      : '-',
    amount: formatMoney(item.amountAfterDiscount ?? 0, item.currency),
  } as LineItemsDataSource<ColKey>;

  if (hasDiscount) {
    row.creditSubtotal = formatMoney(item.amount, item.currency);
    row.discount =
      typeof item.discountAmountExcludeTax === 'number'
        ? `-${formatMoney(item.discountAmountExcludeTax, item.currency)}`
        : '-';
  }

  row.tax = <TaxTooltip amount={item.taxAmount} currency={item.currency} effectiveTaxPercent={item.taxPercent} />;

  return row;
};

const CreditNoteLineItemsV2: React.FC<{
  creditNote: CreditNoteDetail;
}> = ({ creditNote }) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const isTabletOrMobile = useTabletOrMobile();

  const lineItemGroups = creditNote.lineItems;
  const hasInvoiceDiscount = useMemo(() => creditNoteHasInvoiceDiscount(creditNote), [creditNote]);
  const hasLineItemDiscount = useMemo(() => creditNoteHasLineItemDiscount(creditNote), [creditNote]);
  const hasDiscount = useMemo(
    () => hasInvoiceDiscount || hasLineItemDiscount,
    [hasInvoiceDiscount, hasLineItemDiscount]
  );
  const { showAllItems, setShowAllItems, showMoreButtonShown, visibleItems } = useShowMoreItems(
    lineItemGroups,
    isMobile,
    isTabletOrMobile
  );

  const creditWithItem = useMemo(() => visibleItems.some((item) => creditWithLineItem(item)), [visibleItems]);

  const fecColumns: LineItemsColumn<ColKey>[] = useMemo(() => {
    const commonWidth = getCommonWidth(hasDiscount);
    const columns: LineItemsColumn<ColKey>[] = [
      {
        key: 'product',
        header: (<HeaderText testId="col-product">{t('creditNote.product')}</HeaderText>) as unknown as string,
      },
    ];

    if (creditWithItem) {
      columns.push(
        {
          key: 'quantity',
          header: (<HeaderText testId="col-quantity">{t('creditNote.quantity')}</HeaderText>) as unknown as string,
          width: 74,
        },
        {
          key: 'unitPrice',
          header: (
            <HeaderText exclTax testId="col-unitPrice">
              {t('creditNote.unitPrice')}
            </HeaderText>
          ) as unknown as string,
          align: 'right',
          width: commonWidth,
        }
      );
    }

    if (hasDiscount) {
      columns.push({
        key: 'creditSubtotal',
        header: (
          <HeaderText exclTax testId="col-creditSubtotal">
            {t('creditNote.creditSubtotalColumn')}
          </HeaderText>
        ) as unknown as string,
        align: 'right',
        width: Math.max(commonWidth, 100),
      });
      columns.push({
        key: 'discount',
        header: (
          <HeaderText
            exclTax
            testId="col-discount"
            tooltip={
              hasInvoiceDiscount ? (
                <DiscountColumnTooltip
                  discounts={creditNote.discounts}
                  hasLineItemDiscounts={hasLineItemDiscount}
                  currency={creditNote.currency}
                />
              ) : undefined
            }
          >
            {t('invoice.discounts')}
          </HeaderText>
        ) as unknown as string,
        align: 'right',
        width: commonWidth,
      });
    }

    columns.push({
      key: 'amount',
      header: (
        <HeaderText
          exclTax
          testId="col-amount"
          tooltip={!creditWithItem ? t('creditNote.creditAmountTooltip') : undefined}
        >
          {t('creditNote.creditAmount')}
        </HeaderText>
      ) as unknown as string,
      align: 'right',
      width: !creditWithItem ? 120 : Math.max(commonWidth, 100),
    });

    columns.push({
      key: 'tax',
      header: (<HeaderText testId="col-tax">{t('creditNote.tax')}</HeaderText>) as unknown as string,
      align: 'right',
      width: commonWidth,
    });

    return columns;
  }, [
    hasDiscount,
    hasLineItemDiscount,
    hasInvoiceDiscount,
    creditWithItem,
    creditNote.discounts,
    creditNote.currency,
    t,
  ]);

  const fecDataSources: LineItemsDataSource<ColKey>[] = useMemo(() => {
    const rows: LineItemsDataSource<ColKey>[] = [];

    visibleItems.forEach((item) => {
      rows.push(createCreditNoteItemRow(item, hasDiscount));

      if (item.lineItemDiscounts && item.lineItemDiscounts.length > 0) {
        item.lineItemDiscounts.forEach((discount) => {
          rows.push({
            product: <DiscountCell discount={discount} variant="label" useVStack={false} />,
          } as LineItemsDataSource<ColKey>);
        });
      }
    });

    if (showMoreButtonShown) {
      rows.push({
        product: <ShowMoreRow showAllItems={showAllItems} setShowAllItems={setShowAllItems} />,
      } as LineItemsDataSource<ColKey>);
    }

    return rows;
  }, [visibleItems, hasDiscount, showMoreButtonShown, showAllItems, setShowAllItems]);

  return (
    <LineItemsWrapper
      data-test="credit-note-line-items"
      showMoreButtonShown={showMoreButtonShown}
      isMobile={isTabletOrMobile}
    >
      <LineItemsComponent isMobile={isTabletOrMobile} columns={fecColumns} dataSources={fecDataSources} />
    </LineItemsWrapper>
  );
};

export default CreditNoteLineItemsV2;
